import { useCallback, useRef, useState } from 'react';
import { commitManifest, type CommitMutators } from './runner';
import { newRowSteps, updateRowSteps } from './sitePipelines';
import type { CommitSummary, RowCommitState } from './types';
import type { CommitGeographyInput } from './types';
import {
    useCreateCompleteSiteMutation,
    useCreateGeographyMutation,
    useSetActivityMutation,
    useUpdateSiteMutation,
    type GeographyFeatureBody,
    type PreviewSiteManifestResponse,
} from '../state/api/organisationEngineSlice';

export type UseCommit = {
    /** Per-row state keyed by 1-based row number. Updates as orchestration progresses. */
    rowStates: Record<number, RowCommitState>;
    /** True while a commit is in flight. */
    isCommitting: boolean;
    /** Final summary after the last completed run; `null` until the first run settles. */
    summary: CommitSummary | null;
    /** Kicks off the commit. */
    commit: () => Promise<void>;
    /**
     * Clears row state + summary so a fresh upload starts from a clean slate.
     * Without this, a re-upload after a failed commit would still display the
     * stale failure banner because the hook's internal state outlives the
     * route-state replacement.
     */
    reset: () => void;
};

type Args = {
    bcId: string;
    preview: PreviewSiteManifestResponse;
};

/** The flat lat/lng a manifest row carries, in the GeoJSON Feature shape the
 *  engine's geography writes accept — note the [longitude, latitude] order.
 *  Both write paths go through here: the geography leg of a complete site and
 *  the standalone geography write an `update` row makes. */
const asPointFeature = ({ latitude, longitude }: CommitGeographyInput): GeographyFeatureBody => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [longitude, latitude] },
    properties: {},
});

/**
 * Glues the engine's per-resource RTK Query mutations to the generic step
 * harness. This is also where the adapter from the pipeline's typed inputs
 * (`CommitSiteInput`, `CommitGeographyInput`, `CommitActivityInput`) to the
 * engine's wire-format request bodies happens — single-source-of-truth for
 * the `projectCode → business_central_id` and `isicCode → isic_code` rename.
 *
 * Maintains per-row state via a ref so successive step transitions inside a
 * single run don't get lost to React batching.
 */
const useCommit = ({ bcId, preview }: Args): UseCommit => {
    const [createCompleteSite] = useCreateCompleteSiteMutation();
    const [updateSite] = useUpdateSiteMutation();
    const [createGeography] = useCreateGeographyMutation();
    const [setActivity] = useSetActivityMutation();

    const [rowStates, setRowStates] = useState<Record<number, RowCommitState>>({});
    const [isCommitting, setIsCommitting] = useState(false);
    const [summary, setSummary] = useState<CommitSummary | null>(null);

    // The orchestration reports a fresh state object per step. Funnel it
    // through a ref so successive updates don't lose anything to React
    // batching.
    const rowStatesRef = useRef<Record<number, RowCommitState>>({});
    // Synchronous re-entrancy guard. `isCommitting` (state) flips on a
    // re-render cycle, so a double-click that fires two commit() calls in
    // quick succession would both see isCommitting=false and start two
    // concurrent runs that clobber each other's rowStatesRef.
    const isRunningRef = useRef(false);

    const commit = useCallback(async () => {
        if (isRunningRef.current) return;
        isRunningRef.current = true;
        setIsCommitting(true);
        setSummary(null);

        const mutators: CommitMutators = {
            createCompleteSite: ({ site, geography, activity }) =>
                createCompleteSite({ bcId, body: { ...site, geography: asPointFeature(geography), activity } })
                    .unwrap()
                    .then(s => ({ id: s.id })),
            updateSite: (siteId, body) =>
                updateSite({ bcId, siteId, body })
                    .unwrap()
                    .then(() => undefined),
            createGeography: (siteId, input) =>
                createGeography({ bcId, siteId, body: asPointFeature(input) })
                    .unwrap()
                    .then(() => undefined),
            setActivity: (siteId, body) =>
                setActivity({ bcId, siteId, body })
                    .unwrap()
                    .then(() => undefined),
        };

        rowStatesRef.current = {};

        const finalSummary = await commitManifest({
            preview,
            mutators,
            newRowSteps,
            updateRowSteps,
            onRowUpdate: state => {
                rowStatesRef.current = { ...rowStatesRef.current, [state.rowNumber]: state };
                setRowStates(rowStatesRef.current);
            },
        });

        setSummary(finalSummary);
        setIsCommitting(false);
        isRunningRef.current = false;
    }, [bcId, preview, createCompleteSite, updateSite, createGeography, setActivity]);

    const reset = useCallback(() => {
        rowStatesRef.current = {};
        isRunningRef.current = false;
        setRowStates({});
        setSummary(null);
        setIsCommitting(false);
    }, []);

    return { rowStates, isCommitting, summary, commit, reset };
};

export default useCommit;
